<script lang="ts" setup>
import type { PropType } from "vue"
import type { MessageRecordType, MessageStatus } from "@/api/business/types/message"
import { useVModel } from "@vueuse/core"
import { ElButton, ElIcon, ElMessage } from "element-plus"
import { computed, ref } from "vue"
import { batchDeleteReadMessage, batchReadMessage, updateMessage } from "@/api/business/message"
import { MessageStatusConstants } from "@/api/business/types/message"
import { getMessageContent } from "@/components/Notify/messageHandler"
import { getShortDate } from "@/utils/datetime"
// JZTODO store 报错
// import { handleMessage } from "@/components/Notify/messageHandler"

const emit = defineEmits(["update:visible", "refresh"])

const props = defineProps({
  messageList: {
    type: Object as PropType<MessageRecordType[]>,
    required: true,
  },
  visible: {
    type: Boolean,
    required: true,
  },
})

// 使用useVModel来处理visible属性
const isVisible = useVModel(props, "visible", emit)

const sortedMessageList = computed(() => {
  return [...props.messageList].filter(message => {
    if (messageFilter.value === 'all') {
      return true
    }
    if (messageFilter.value === 'read') {
      return message.messageStatus === MessageStatusConstants.PROCESSED
    }
    if (messageFilter.value === 'unread') {
      return message.messageStatus === MessageStatusConstants.NOT_PROCESSED
    }
    return false
  }).sort((a, b) => {
    if (a.createTime && b.createTime) {
      return new Date(b.createTime).getTime() - new Date(a.createTime).getTime()
    } else {
      return 0
    }
  })
})

const handleClose = () => {
  isVisible.value = false
}

const handleDetail = (_message: MessageRecordType) => {
  // handleMessage(message)
}

const handleUpdateStatus = (messageId: number, status: MessageStatus) => {
  updateMessage({
    merchantMessageRecordId: messageId,
    messageStatus: status,
  })
    .then(() => {
      ElMessage.success("操作成功")
      emit("refresh")
    })
    .catch(() => {
      ElMessage.error("操作失败")
    })
}

const messageOptions = [
  { value: "all", label: "全部消息", icon: 'nav-notification' },
  { value: "unread", label: "未读消息", icon: 'nav-notification_unread' },
  { value: "read", label: "已读消息", icon: 'nav-notification' },
]

const messageFilter = ref("all")
const messageFilterLabel = computed(() => {
  const currentOption = messageOptions.find(option => {
    return option.value === messageFilter.value
  })
  return currentOption?.label
})

const handleFilterChange = (value: string) => {
  messageFilter.value = value
}

const handleClearAllReadMessage = () => {
  batchDeleteReadMessage()
    .then(() => {
      ElMessage.success("操作成功")
      emit("refresh")
    })
    .catch(() => {
      ElMessage.error("操作失败")
    })
}

const handleMarkAllRead = () => {
  batchReadMessage()
    .then(() => {
      ElMessage.success("操作成功")
      emit("refresh")
    })
    .catch(() => {
      ElMessage.error("操作失败")
    })
}
</script>

<template>
  <el-drawer v-model="isVisible" direction="rtl" size="400px" @close="handleClose" class="notify-list-drawer" :append-to-body="true">
    <template #header>
      <div class="notify-list-title text-size-20px">消息管理</div>
    </template>
    <div class="message-filter">
      <el-dropdown @command="handleFilterChange" trigger="click">
        <span class="hover-effect cursor-pointer">
          {{ messageFilterLabel }}
          <el-icon>
            <ArrowDown />
          </el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item v-for="item in messageOptions" :key="item.value" :command="item.value">
              <svg-icon :name="item.icon" size="20px" mr-8px /> {{ item.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>

      <el-dropdown :trigger="$mediaInfo.isMobile ? 'click' : 'hover'" placement="bottom-start">
        <span class="hover-effect cursor-pointer">
          <el-button text class="hover-effect">
            <el-icon><More /></el-icon>
          </el-button>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item @click="handleMarkAllRead">
              <el-icon><Finished /></el-icon>
              <span ml-1>标记所有为已读</span>
            </el-dropdown-item>
            <el-dropdown-item @click="handleClearAllReadMessage">
              <el-icon><Delete /></el-icon>
              <span ml-1>删除所有已读消息</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <div class="message-list">
      <el-empty v-if="sortedMessageList.length === 0" description="暂无消息" />
      <template v-else>
        <div v-for="item in sortedMessageList" :key="item.merchantMessageRecordId" class="message-item flex flex-col" :class="{ unread: item.messageStatus === MessageStatusConstants.NOT_PROCESSED }">
          <div class="message-header">
            <div class="message-title">
              {{ item.messageTitle }}
            </div>
            <div class="message-actions text-size-20px">
              <el-icon v-if="item.messageStatus === MessageStatusConstants.NOT_PROCESSED" class="hover-effect mr-1em cursor-pointer" @click="handleUpdateStatus(item.merchantMessageRecordId!, MessageStatusConstants.PROCESSED)"><Finished /></el-icon>
              <el-icon class="hover-effect cursor-pointer" @click="handleUpdateStatus(item.merchantMessageRecordId!, MessageStatusConstants.DELETED)"><Close /></el-icon>
            </div>
          </div>
          <div class="message-description m-auto flex flex-grow-1 items-center">{{ getMessageContent(item) }}</div>
          <div class="flex items-center justify-between">
            <span class="message-time">{{ getShortDate(item.createTime!, 'YYYY-MM-DD', 'HH:mm') }}</span>
            <el-button v-if="false" type="primary" size="small" @click="handleDetail(item)" style="width: 88px;height: 40px">
              去处理
            </el-button>
          </div>
        </div>
      </template>
    </div>
  </el-drawer>
</template>

<style lang="scss">
.notify-list-drawer {
  color: var(--tea-color-text-regular);
  background: var(--tea-color-bg-surface);

  .notify-list-title {
    font-weight: 600;
    color: var(--tea-color-text-primary);
  }

  .el-drawer__body {
    padding: 0;
  }
}
</style>

<style lang="scss" scoped>
.message-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  color: var(--tea-color-text-regular);
  border-bottom: 1px solid var(--tea-color-border);
}

.message-list {
  .message-item {
    --text-color: var(--tea-color-text-secondary);

    min-height: 180px;
    padding: 16px;
    border-bottom: 1px solid var(--tea-color-border);

    &.unread {
      --text-color: var(--tea-color-text-primary);
    }

    .message-header {
      display: flex;
      justify-content: space-between;
      font-size: 16px;

      .message-title {
        color: var(--text-color);
      }
    }

    .message-actions {
      color: var(--tea-color-text-regular);
    }

    .message-time {
      font-size: 14px;
      color: var(--text-color);
    }

    .message-description {
      font-size: 16px;
      color: var(--text-color);
    }
  }
}

.hover-effect {
  transition: color 0.3s ease;

  &:hover {
    color: var(--tea-color-primary);
  }
}

.popover-content {
  display: flex;
  flex-direction: column;

  .el-button {
    justify-content: flex-start;
    padding: 8px 0;
  }
}
</style>
